// libs
import React, { useCallback, useState } from 'react';
import { css } from '@emotion/css';

import { useTranslation } from '@lyearn/i18n';
import { Certificate2Icon } from '@lyearn/icons';
import { EmptyState } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
// hooks
import { Stack } from '@lyearn/ui';
// components
import Loader from '@/components/Loader';
import VirtualizedList from '@/components/VirtualizedList';
import BadgeCard from '@/modules/corporate/modules/badges/components/BadgeCard';
import useBadgeTemplates from '@/modules/corporate/modules/badges/hooks/useBadgeTemplates';
import { BadgeStatusEnum, BadgeTypeEnum } from '@/types/schema';
export const listContainerStyles = css`
  > div > div {
    padding-inline-end: 2.4rem;
  }
`;
function Badges(props: { value?: string[]; onChange: (badgeIds: string[]) => void }) {
  const { t } = useTranslation(['Course']);
  const { onChange: onAttach, value: selectedBadgeIds } = props;
  const [searchText, setSearchText] = useState<string>();
  const { badges, initialFetching, fetchMore, totalCount } = useBadgeTemplates({
    filters: {
      parentEntityIdExists: false,
      status: [BadgeStatusEnum.Active],
      conditional: false,
      type: [BadgeTypeEnum.Badge],
    },
  });
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set(selectedBadgeIds ?? []));

  const onSelect = useCallback(
    (badgeId: string, isSelected?: boolean) => {
      const selectedItemsSet = new Set(selectedItems);
      if (isSelected) {
        selectedItemsSet.delete(badgeId);
      } else {
        selectedItemsSet.add(badgeId);
      }
      setSelectedItems(selectedItemsSet);
      onAttach(Array.from(selectedItemsSet));
    },
    [selectedItems],
  );

  const renderer = useCallback(() => {
    if (initialFetching) {
      return <Loader className="mt-24" />;
    }

    if (!badges?.length) {
      if (searchText) {
        return (
          <EmptyState
            Icon={Certificate2Icon}
            className="h-full w-full"
            description={t('Course:Form.NoCertificatesToAwardDescription')}
            title={t('Course:Form.NoCertificatesToAward')}
          />
        );
      }

      return (
        <EmptyState
          Icon={Certificate2Icon}
          className="h-full w-full"
          description={t('Course:Form.NoCertificatesToAwardDescription')}
          title={t('Course:Form.NoCertificatesToAward')}
        />
      );
    }

    return (
      <>
        <VirtualizedList
          className={cn('!w-[59.4rem] !h-[43rem]')}
          data={badges}
          endReached={fetchMore}
          itemContent={(index) => {
            const currentBadge = badges?.[index];
            if (!currentBadge) {
              return null;
            }

            const isSelected = selectedItems.has(currentBadge.id);
            return (
              <BadgeCard
                badge={currentBadge}
                disabled={false}
                isSelected={isSelected}
                onSelect={onSelect}
              />
            );
          }}
        />
      </>
    );
  }, [initialFetching, badges, searchText, fetchMore, totalCount, selectedItems]);

  return (
    <Stack className="w-full mt-12" justifyContent="center">
      {renderer()}
    </Stack>
  );
}

export default React.memo(Badges);
